import {
  GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
  METRIC_REGISTRY_GLOBAL_TENANT_ID,
  normalizeIndustryProfileId,
} from './metric-registry';

// THE single governed-registry precedence semantics (graph program Phase 1,
// docs/process/graph-completion-program-20260807.md). Moved verbatim from
// apps/api/src/semantic-layer/registry-resolution.shared.ts (which re-exports
// from here) so the activation-reconciliation receipt script can apply the SAME
// scope/precedence selection production uses — a receipt computing answerability
// with different semantics would certify metrics the real loader refuses.
//
// Canonical semantics (deliberate choices, do not weaken):
// - Layer dominates: company > industry > core. A specificity or tenant bonus can
//   never jump a layer (bonuses max 32 < layer step 100).
// - Tenant-specific rows outrank `__global__` rows within a layer.
// - Scope-specific industryProfileId / companyMappingId outrank `__all__` within
//   a layer.
// - A PRESENT-but-unknown registryLayer value does not apply at all; an ABSENT
//   registryLayer stays applicable and ranks as core.
// - Lifecycle breaks TIES ONLY: an ACTIVE row beats an otherwise equally ranked
//   non-ACTIVE twin. It never crosses a structural gap, so a higher-precedence
//   non-ACTIVE row still shadows a lower ACTIVE sibling and still fails closed.

export type RegistryPrecedenceRow = Readonly<{
  tenantId?: string;
  registryLayer?: string;
  industryProfileId?: string;
  companyMappingId?: string;
  /** Lifecycle. Absent ranks as non-ACTIVE, which changes no existing order. */
  status?: string;
}>;

export type RegistryResolutionScope = Readonly<{
  tenantId: string;
  industryProfileId?: string | null;
  companyMappingId?: string | null;
}>;

const KNOWN_REGISTRY_LAYERS = new Set(['core', 'industry', 'company']);

export function registryRowApplies(
  row: RegistryPrecedenceRow,
  scope: RegistryResolutionScope,
): boolean {
  const tenantId = row.tenantId ?? METRIC_REGISTRY_GLOBAL_TENANT_ID;
  const industryProfileId =
    row.industryProfileId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY;
  const companyMappingId =
    row.companyMappingId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY;
  return (
    (row.registryLayer === undefined ||
      KNOWN_REGISTRY_LAYERS.has(row.registryLayer)) &&
    (tenantId === METRIC_REGISTRY_GLOBAL_TENANT_ID ||
      tenantId === scope.tenantId) &&
    // The 3PL profile's two governed spellings match through the ONE declared
    // translation (CANONICAL_INDUSTRY_PROFILE_ID_BY_LEGACY_ALIAS) — persisted
    // identities and digests untouched; only this scope comparison normalizes.
    (industryProfileId === GOVERNED_METRIC_FACT_ALL_MEMBER_KEY ||
      normalizeIndustryProfileId(industryProfileId) ===
        normalizeIndustryProfileId(scope.industryProfileId ?? undefined)) &&
    (companyMappingId === GOVERNED_METRIC_FACT_ALL_MEMBER_KEY ||
      companyMappingId === scope.companyMappingId)
  );
}

export function registryRowRank(row: RegistryPrecedenceRow): number {
  const layerRank =
    row.registryLayer === 'company' ? 200 : row.registryLayer === 'industry' ? 100 : 0;
  const tenantRank =
    row.tenantId && row.tenantId !== METRIC_REGISTRY_GLOBAL_TENANT_ID ? 20 : 0;
  const companyRank =
    row.companyMappingId &&
    row.companyMappingId !== GOVERNED_METRIC_FACT_ALL_MEMBER_KEY
      ? 8
      : 0;
  const industryRank =
    row.industryProfileId &&
    row.industryProfileId !== GOVERNED_METRIC_FACT_ALL_MEMBER_KEY
      ? 4
      : 0;
  // Canonical-spelling tiebreak: after a profile re-mint the DEPRECATED
  // legacy-alias row and the re-minted canonical row are BOTH applicable to
  // either request spelling (the alias translation makes them equi-applicable)
  // and would otherwise tie — resolution then throws metric_registry_ambiguous
  // before its lifecycle check ever runs, making the remediated dimension
  // unusable. The canonically spelled row wins the tie so selection reaches the
  // lifecycle check (ACTIVE canonical resolves; a deprecated canonical fails
  // closed as inactive). The bonus (1) is smaller than every structural gap
  // (4), so no non-tied ordering changes.
  const canonicalSpellingRank =
    row.industryProfileId &&
    row.industryProfileId !== GOVERNED_METRIC_FACT_ALL_MEMBER_KEY &&
    normalizeIndustryProfileId(row.industryProfileId) === row.industryProfileId
      ? 1
      : 0;
  // #5305 run 7 P0 — LIFECYCLE TIEBREAK. `resolveActiveMetric` ranks every
  // applicable row regardless of lifecycle and only then requires the winner to
  // be ACTIVE. So one ACTIVE `metric_registry_v1` row and one DEPRECATED
  // `objective_value_bridge_v1` row for the same key at the same scope
  // (core/__global__/__all__) tied exactly, and the tie threw
  // `metric_registry_ambiguous` BEFORE the lifecycle check could run. That is a
  // non-absence error, so metric-identity marked the metric failed and the
  // decision package was withheld `registry_identity_unavailable` on live keys
  // (operating_income_usd, objective_gap_usd, and two more).
  //
  // Same shape as the canonical-spelling tiebreak above, and deliberately
  // ordered against it: the bonus (2) is LARGER than the spelling bonus (1) so
  // an ACTIVE legacy-spelled row beats a DEPRECATED canonical one, and SMALLER
  // than every structural gap (4) so no layer, tenant or scope ordering moves.
  // A key whose only applicable row is non-ACTIVE still reaches the lifecycle
  // check and still fails closed as `metric_registry_inactive`.
  const lifecycleRank = row.status === 'ACTIVE' ? 2 : 0;
  return (
    layerRank +
    tenantRank +
    companyRank +
    industryRank +
    lifecycleRank +
    canonicalSpellingRank
  );
}
